(() => {
  const MAX_FILES = 5;
  const MAX_SIZE = 2 * 1024 * 1024; // 2MB
  const ALLOWED_EXT = ['jpg', 'jpeg', 'pdf', 'tif', 'tiff', 'png'];

  function getExt(filename) {
    return (filename.split('.').pop() || '').toLowerCase();
  }

  function validateFiles(files) {
    if (!files.length) return 'Please attach at least one file.';
    if (files.length > MAX_FILES) return `You can attach up to ${MAX_FILES} files.`;
    for (const file of files) {
      if (!ALLOWED_EXT.includes(getExt(file.name))) {
        return 'Only JPEG, PDF, TIFF or PNG files are accepted.';
      }
      if (file.size > MAX_SIZE) {
        return `"${file.name}" is larger than 2MB.`;
      }
    }
    return '';
  }

  function initDropzone(form) {
    const dropzone = form.querySelector('[data-nc-dropzone]');
    const input = form.querySelector('[data-nc-file-input]');
    const textEl = form.querySelector('[data-nc-dropzone-text]');
    const errorEl = form.querySelector('[data-nc-file-error]');
    const attachmentTrigger = form.querySelector('[data-nc-attachment-trigger]');
    if (!dropzone || !input) return;

    const defaultText = textEl ? textEl.textContent : '';

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = !message;
    }

    function renderFiles() {
      const files = Array.from(input.files || []);
      if (!textEl) return;
      textEl.textContent = files.length
        ? files.map((f) => f.name).join(', ')
        : defaultText;
    }

    function handleFiles(fileList) {
      const error = validateFiles(fileList);
      if (error) {
        showError(error);
        input.value = '';
        renderFiles();
        return;
      }
      showError('');
      input.setCustomValidity('');
      renderFiles();
    }

    input.addEventListener('change', () => handleFiles(input.files));

    attachmentTrigger?.addEventListener('click', () => input.click());

    ['dragenter', 'dragover'].forEach((evt) => {
      dropzone.addEventListener(evt, (e) => {
        e.preventDefault();
        dropzone.classList.add('is-dragover');
      });
    });

    ['dragleave', 'dragend'].forEach((evt) => {
      dropzone.addEventListener(evt, () => dropzone.classList.remove('is-dragover'));
    });

    dropzone.addEventListener('drop', (e) => {
      e.preventDefault();
      dropzone.classList.remove('is-dragover');
      const dropped = e.dataTransfer?.files;
      if (!dropped || !dropped.length) return;
      input.files = dropped;
      handleFiles(input.files);
    });
  }

  function initChoiceGroups(form) {
    form.querySelectorAll('[data-nc-choice-group]').forEach((group) => {
      const valueInput = group.querySelector('[data-nc-choice-value]');
      const buttons = group.querySelectorAll('[data-nc-choice]');

      buttons.forEach((btn) => {
        btn.addEventListener('click', () => {
          buttons.forEach((b) => b.classList.remove('is-selected'));
          btn.classList.add('is-selected');
          if (valueInput) {
            valueInput.value = btn.dataset.ncChoice;
            valueInput.setCustomValidity('');
          }
        });
      });
    });
  }

  // Custom-styled dropdown for Carat Range / Ring Size — replaces the
  // native <select> so we get full control over the option list's look
  // (native select option lists can't be styled, and their browser
  // focus ring renders as a plain black box).
  function initDropdownSelects(form) {
    const dropdowns = form.querySelectorAll('[data-nc-dropdown]');

    function closeAll(except) {
      dropdowns.forEach((dd) => {
        if (dd === except) return;
        dd.removeAttribute('data-open');
        dd.querySelector('[data-nc-dropdown-trigger]')?.setAttribute('aria-expanded', 'false');
        const panel = dd.querySelector('[data-nc-dropdown-panel]');
        if (panel) panel.hidden = true;
      });
    }

    dropdowns.forEach((dropdown) => {
      const valueInput = dropdown.querySelector('[data-nc-dropdown-value]');
      const trigger = dropdown.querySelector('[data-nc-dropdown-trigger]');
      const label = dropdown.querySelector('[data-nc-dropdown-label]');
      const panel = dropdown.querySelector('[data-nc-dropdown-panel]');
      const options = dropdown.querySelectorAll('[data-nc-dropdown-option]');
      if (!trigger || !panel) return;

      trigger.addEventListener('click', () => {
        const isOpen = dropdown.hasAttribute('data-open');
        closeAll(dropdown);
        if (isOpen) {
          dropdown.removeAttribute('data-open');
          trigger.setAttribute('aria-expanded', 'false');
          panel.hidden = true;
        } else {
          dropdown.setAttribute('data-open', '');
          trigger.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
        }
      });

      options.forEach((option) => {
        option.addEventListener('click', () => {
          const value = option.dataset.ncDropdownOption;
          options.forEach((o) => o.classList.remove('is-selected'));
          option.classList.add('is-selected');
          if (label) label.textContent = value;
          if (valueInput) {
            valueInput.value = value;
            valueInput.setCustomValidity('');
          }
          trigger.classList.add('has-value');
          dropdown.removeAttribute('data-open');
          trigger.setAttribute('aria-expanded', 'false');
          panel.hidden = true;
          trigger.focus();
        });
      });
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-nc-dropdown]')) closeAll();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeAll();
    });
  }

  // Stop Enter key from submitting the form early while the user is still
  // filling out other fields — only the actual submit button should submit.
  function blockEnterSubmit(form) {
    form.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      const target = e.target;
      if (target.tagName === 'TEXTAREA' || target.tagName === 'BUTTON') return;
      e.preventDefault();
    });
  }

  // Shopify's contact form accepts individual file fields named
  // contact[attachment1] .. contact[attachmentN] — split our single
  // multi-file picker into those before the form submits for real.
  function attachFilesToForm(form, input) {
    const files = Array.from(input.files || []);
    files.slice(0, MAX_FILES).forEach((file, index) => {
      const hiddenInput = document.createElement('input');
      hiddenInput.type = 'file';
      hiddenInput.name = `contact[attachment${index + 1}]`;
      hiddenInput.classList.add('nc-visually-hidden');

      const dt = new DataTransfer();
      dt.items.add(file);
      hiddenInput.files = dt.files;

      form.appendChild(hiddenInput);
    });
    input.disabled = true;
  }

  function initFormSubmit(form) {
    const fileInput = form.querySelector('[data-nc-file-input]');
    const fileErrorEl = form.querySelector('[data-nc-file-error]');

    // Submitting natively (no fetch/AJAX) on purpose: Shopify's contact
    // form is protected by hCaptcha/bot-detection tied to a real page
    // navigation. Intercepting it with fetch() gets the request blocked.
    //
    // Shopify's own anti-spam script also listens for this form's submit
    // event and, asynchronously, forces the real submission through once
    // its captcha check resolves — regardless of what runs after it. So
    // when validation fails we listen in the CAPTURE phase and call
    // stopImmediatePropagation() to stop that script's listener from ever
    // running; only a fully valid submit is allowed to reach it.
    form.addEventListener(
      'submit',
      (e) => {
        if (!form.checkValidity()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          form.reportValidity();
          return;
        }

        const fileError = validateFiles(fileInput ? fileInput.files : []);
        if (fileError) {
          e.preventDefault();
          e.stopImmediatePropagation();
          if (fileErrorEl) {
            fileErrorEl.textContent = fileError;
            fileErrorEl.hidden = false;
          }
          fileInput?.focus();
          return;
        }

        if (fileInput) attachFilesToForm(form, fileInput);
        // Valid — don't stop propagation or preventDefault; let the
        // browser (and any other submit listeners) proceed normally.
      },
      { capture: true }
    );
  }

  function initModal(modal) {
    if (!modal) return null;

    let autoCloseTimer = null;

    function open(autoCloseMs) {
      modal.hidden = false;
      document.body.style.overflow = 'hidden';

      if (autoCloseTimer) clearTimeout(autoCloseTimer);
      if (autoCloseMs) {
        autoCloseTimer = setTimeout(close, autoCloseMs);
      }
    }

    function close() {
      if (autoCloseTimer) {
        clearTimeout(autoCloseTimer);
        autoCloseTimer = null;
      }
      modal.hidden = true;
      document.body.style.overflow = '';
    }

    modal.querySelectorAll('[data-nc-dream-modal-close]').forEach((el) => {
      el.addEventListener('click', close);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.hidden) close();
    });

    return { open, close };
  }

  function init() {
    const form = document.getElementById('NcDreamRingForm');
    const modalEl = document.querySelector('[data-nc-dream-modal]');
    if (!form) return;

    blockEnterSubmit(form);
    initDropzone(form);
    initChoiceGroups(form);
    initDropdownSelects(form);
    initFormSubmit(form);

    const modal = initModal(modalEl);

    if (document.querySelector('[data-nc-dream-success]') && modal) {
      modal.open(5000);
      if (window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.delete('contact_posted_successfully');
        window.history.replaceState({}, '', url.toString());
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
